import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, connect } from 'react-redux';
import { ExpandMore, ExpandLess } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { findBulletinsData } from 'reducers/BulletinReducer';
import { getPeopleSops } from 'reducers/SearchReducer';
import { searchWarrants } from 'reducers/SearchReducer';
import { showWarrants } from 'reducers/PersonDialogsReducer';

const useStyles = makeStyles((theme) => ({
  contentWrap: {
    minWidth: 800,
    width: '100%',
    boxSizing: 'border-box',
    height: 600,
  },
  urgentMessageContainer: {
    width: '100%',
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 15,
  },

  urgentMessageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.type === 'dark' ? '#7f1d1d' : '#d32f2f',
    color: '#ffffff',
    padding: '10px 16px',
    cursor: 'pointer',
    minHeight: 44,
    boxSizing: 'border-box',

    '&:hover': {
      backgroundColor: theme.palette.type === 'dark' ? '#5c1414' : '#b71c1c',
    },
  },

  urgentMessageTitle: {
    fontSize: 16,
    fontWeight: 600,
  },

  urgentMessageArrow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
  },

  urgentMessageContent: {
    border: `1px solid ${theme.palette.type === 'dark' ? '#424242' : '#e0e0e0'}`,
    borderTop: 'none',
    backgroundColor: theme.palette.type === 'dark' ? '#1e1e1e' : '#ffffff',
  },

  urgentMessageRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.palette.type === 'dark' ? '#424242' : '#e0e0e0'}`,

    '&:last-child': {
      borderBottom: 'none',
    },
  },

  urgentMessageText: {
    fontSize: 14,
    color: theme.palette.type === 'dark' ? '#e0e0e0' : '#333333',
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.type === 'dark' ? '#90caf9' : '#1976d2',
    },
  },
}));

const UrgentMessage = (props) => {
  const {
    ptsPersonID,
    reloadUrgentMsg,
    onActiveWarrantsClick,
    onSopClick,
    onActiveBulletinsClick,
  } = props;
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);

  const urgentMsg = [
    {
      key: 'activeWarrants',
      title: 'Active Warrants',
      data: [],
      count: 0,
      onClick: onActiveWarrantsClick,
    },
    { key: 'sop', title: 'SOP', data: [], count: 0, onClick: onSopClick },
    {
      key: 'activeBulletins',
      title: 'Active Bulletins',
      data: [],
      count: 0,
      onClick: onActiveBulletinsClick,
    },
  ];

  const [urgentMessages, setUrgentMessages] = useState(urgentMsg);
  const { page, pageSize } = useSelector((state) => state.warrant.pagination);

  useEffect(() => {
    if (ptsPersonID) getUrgentMessage();
    else return null;
  }, [ptsPersonID, reloadUrgentMsg]);

  const totalCount = useMemo(() => {
    return urgentMessages.reduce((sum, item) => sum + item.count, 0);
  }, [urgentMessages]);

  const getActiveWarrants = async () => {
    return searchWarrants(
      {
        activeOnly: true,
        ptsPersonID,
      },
      page,
      pageSize
    );
  };

  const getUrgentMessage = async () => {
    try {
      const [warrantsData, sopResponse, bulletinsData] = await Promise.all([
        getActiveWarrants(),
        getPeopleSops(ptsPersonID),
        findBulletinsData({ ptsPersonID }),
      ]);

      const urgentData = {
        activeWarrants: warrantsData ?? [],
        sop: sopResponse?.data ?? [],
        activeBulletins: bulletinsData ?? [],
      };

      setUrgentMessages((prev) =>
        prev.map((item) => {
          const data = urgentData[item.key];
          if (!data) return item;

          const updatedItem = {
            ...item,
            data,
            count: data.length,
          };

          return updatedItem;
        })
      );
    } catch (error) {
      console.error('Error fetching urgent messages:', error);
    }
  };

  const handleToggle = () => setExpanded((prev) => !prev);

  const handleUrgentMessageClick = (message) => {
    if (message.key === 'activeWarrants') {
      props.showWarrants({ warrants: message.data });
    } else {
      message.onClick && message.onClick(message);
    }
  };

  return (
    <>
      {totalCount > 0 && (
        <div className={classes.urgentMessageContainer}>
          {/* Header */}
          <div
            className={classes.urgentMessageHeader}
            onClick={handleToggle}
            role="button"
            tabIndex={0}>
            <span className={classes.urgentMessageTitle}>Urgent Message ({totalCount})</span>

            <span className={classes.urgentMessageArrow}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </span>
          </div>

          {/* Expandable Content */}
          {expanded && (
            <div className={classes.urgentMessageContent}>
              {urgentMessages.map(
                (message, index) =>
                  message?.count > 0 && (
                    <div className={classes.urgentMessageRow} key={message.id || index}>
                      <span
                        className={classes.urgentMessageText}
                        onClick={() => handleUrgentMessageClick(message)}>
                        {message.title} ({message.count})
                      </span>
                    </div>
                  )
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default connect(null, { showWarrants })(UrgentMessage);
